import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ShieldCheck } from "@phosphor-icons/react";

const STATEMENT = "A criminal conviction does not automatically disqualify an applicant from employment. Criminal history will be evaluated individually in accordance with applicable law and the responsibilities of the position.";
const ACK = "I understand that I am required to provide complete and truthful information during the pre-employment process. If I fail to disclose required criminal-history information, provide false information, or materially misrepresent my criminal history, Silver Home Care may withdraw its conditional offer of employment. Before an offer is withdrawn because of a discrepancy, I will be given an opportunity to explain the discrepancy or correct inaccurate information.";

export default function CriminalDisclosureCard() {
  const { user } = useAuth();
  const prof = user?.profile || {};
  const existing = prof.criminal_disclosure;
  const pending = prof.employment_status === "pending_onboarding";
  const [has, setHas] = useState("");
  const [details, setDetails] = useState("");
  const [ack, setAck] = useState(false);
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!pending && !existing && !done) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/my/criminal-disclosure", { has_record: has, details, acknowledged: ack, certify_name: name });
      toast.success("Disclosure submitted — thank you");
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (existing || done) {
    const d = done ? null : existing;
    return (
      <div className="rounded-xl border border-[#27AE60]/30 bg-[#27AE60]/5 p-5 shadow-sm" data-testid="disclosure-done-card">
        <p className="flex items-center gap-2 text-sm font-semibold text-[#1e8449]"><ShieldCheck size={18} weight="fill" /> Background disclosure on file</p>
        <p className="mt-1 text-xs text-stone-600">
          {done ? "Just submitted" : `Submitted ${(d?.submitted_at || "").slice(0, 10)} by ${d?.certify_name || "you"}`}.
          The office completes an individualized review before any final decision — a conviction does not automatically disqualify you.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="criminal-disclosure-card">
      <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900">
        <ShieldCheck size={20} className="text-primary" /> Pre-Employment Criminal Background Disclosure and Review
      </p>
      <p className="mt-2 text-sm leading-relaxed text-stone-600">
        Please disclose all felony and misdemeanor convictions, and pleas of guilty or no contest, that must legally be disclosed.
        <span className="font-semibold"> Do not</span> report arrests that did not result in a conviction, or records that have been
        expunged, sealed, pardoned, or are otherwise legally protected from disclosure.
      </p>
      <p className="mt-3 rounded-lg bg-accent/40 p-3 text-xs font-medium leading-relaxed text-stone-700" data-testid="disclosure-statement">{STATEMENT}</p>

      <div className="mt-4" data-testid="disclosure-question">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Do you have anything to disclose? <span className="text-primary">*</span></p>
        <div className="flex gap-2">
          {["No", "Yes"].map((v) => (
            <button key={v} type="button" onClick={() => setHas(v.toLowerCase())}
              className={`rounded-full border px-5 py-1.5 text-xs font-semibold transition-colors ${has === v.toLowerCase() ? "border-primary bg-primary text-white" : "border-stone-200 bg-white text-stone-500 hover:border-primary/40"}`}
              data-testid={`disclosure-${v.toLowerCase()}`}>{v}</button>
          ))}
        </div>
      </div>
      {has === "yes" && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-medium text-stone-500">Please list each conviction or plea (what, when, disposition):</p>
          <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} className="border-stone-300" data-testid="disclosure-details" />
        </div>
      )}

      <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="disclosure-ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
        <span className="text-xs leading-relaxed text-stone-700">{ACK}</span>
      </label>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-52">
          <p className="mb-1 text-xs font-medium text-stone-500">Type your full legal name to certify <span className="text-primary">*</span></p>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="border-stone-300" data-testid="disclosure-name" />
        </div>
        <p className="text-xs text-stone-400">Date: {new Date().toLocaleDateString()}</p>
      </div>
      <Button onClick={submit} disabled={busy || !has || (has === "yes" && !details.trim()) || !ack || !name.trim()}
        className="mt-4 rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid="disclosure-submit">
        {busy ? "Submitting…" : "Submit disclosure"}
      </Button>
    </div>
  );
}
